import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import MarketOverviewCards from './components/MarketOverviewCards';
import MarketHeatmap from './components/MarketHeatmap';
import NewsAggregation from './components/NewsAggregation';
import ProfessionalAdvisory from './components/ProfessionalAdvisory';
import TechnicalAnalysisTools from './components/TechnicalAnalysisTools';

const MarketAnalysis = () => {
  const [user, setUser] = useState({
    name: "Alex Thompson",
    email: "alex.thompson@email.com",
    subscriptionTier: "pro"
  });
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');
  const [selectedMarketSegment, setSelectedMarketSegment] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  const timeframeOptions = [
    { value: '1h', label: '1 Hour' },
    { value: '24h', label: '24 Hours' },
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' }
  ];

  const marketSegmentOptions = [
    { value: 'all', label: 'All Markets' },
    { value: 'large-cap', label: 'Large Cap (>$10B)' },
    { value: 'mid-cap', label: 'Mid Cap ($1B-$10B)' },
    { value: 'small-cap', label: 'Small Cap (<$1B)' },
    { value: 'defi', label: 'DeFi Tokens' },
    { value: 'layer1', label: 'Layer 1 Protocols' },
    { value: 'meme', label: 'Meme Coins' }
  ];

  useEffect(() => {
    // Simulate loading time for market data
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const handleLogin = () => {
    window.location.href = '/user-login';
  };

  const handleLogout = () => {
    setUser(null);
    window.location.href = '/user-login';
  };

  const handleUpgrade = () => {
    window.location.href = '/subscription-management';
  };

  const handleRefreshData = () => {
    setIsLoading(true);
    // Simulate data refresh
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const handleExportReport = () => {
    // Simulate report export
    alert('Market analysis report exported successfully!');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} onLogin={handleLogin} onLogout={handleLogout} onUpgrade={handleUpgrade} />
        <div className="pt-16">
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-foreground mb-2">Loading Market Analysis</h2>
              <p className="text-muted-foreground">Fetching real-time market data and insights...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onLogin={handleLogin} onLogout={handleLogout} onUpgrade={handleUpgrade} />
      
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Market Analysis</h1>
              <p className="text-muted-foreground">
                Comprehensive cryptocurrency market insights with AI-powered analysis and professional advisory
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 mt-6 lg:mt-0">
              <div className="flex gap-2">
                <Select
                  options={timeframeOptions}
                  value={selectedTimeframe}
                  onChange={setSelectedTimeframe}
                  placeholder="Select timeframe"
                  className="w-32"
                />
                <Select
                  options={marketSegmentOptions}
                  value={selectedMarketSegment}
                  onChange={setSelectedMarketSegment}
                  placeholder="Market segment"
                  className="w-40"
                />
              </div>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={handleRefreshData}
                >
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={handleExportReport}
                >
                  Export
                </Button>
              </div>
            </div>
          </div>

          {/* Market Overview Cards */}
          <MarketOverviewCards />

          {/* Market Heatmap */}
          <MarketHeatmap />

          {/* News Aggregation */}
          <NewsAggregation />

          {/* Professional Advisory */}
          <ProfessionalAdvisory />

          {/* Technical Analysis Tools */}
          <TechnicalAnalysisTools />

          {/* Educational Resources Section */}
          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-card-foreground mb-2">Educational Resources</h2>
                <p className="text-sm text-muted-foreground">
                  Learn about market indicators and analysis methodologies
                </p>
              </div>
              <Button variant="outline" size="sm">
                View All Resources
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="p-4 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-standard">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="BookOpen" size={20} className="text-primary" />
                  </div>
                  <h3 className="font-medium text-card-foreground">Understanding Correlation</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Learn how cryptocurrency correlations work and their impact on portfolio diversification.
                </p>
              </div>

              <div className="p-4 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-standard">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="TrendingUp" size={20} className="text-accent" />
                  </div>
                  <h3 className="font-medium text-card-foreground">Volatility Analysis</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Discover how to interpret volatility indicators and use them for risk management.
                </p>
              </div>

              <div className="p-4 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-standard">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="Building2" size={20} className="text-success" />
                  </div>
                  <h3 className="font-medium text-card-foreground">Institutional Flow</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Understand how institutional money movements affect cryptocurrency markets.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-semibold text-card-foreground mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Button
                variant="outline"
                fullWidth
                iconName="Target"
                iconPosition="left"
                onClick={() => window.location.href = '/ai-predictions-detail'}
              >
                View AI Predictions
              </Button>
              <Button
                variant="outline"
                fullWidth
                iconName="PieChart"
                iconPosition="left"
                onClick={() => window.location.href = '/portfolio-tracker'}
              >
                Check Portfolio
              </Button>
              <Button
                variant="outline"
                fullWidth
                iconName="Zap"
                iconPosition="left"
                onClick={() => window.location.href = '/trading-signals'}
              >
                Trading Signals
              </Button>
              <Button
                variant="outline"
                fullWidth
                iconName="BarChart3"
                iconPosition="left"
                onClick={() => window.location.href = '/trading-dashboard'}
              >
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketAnalysis;